import React, { useEffect } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { CancelButton, SubmitButton, UpdateButton } from '../../../components/mui/Buttons/Buttons';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import {
  createAchievementBadges,
  updateAchievementBadges,
  uploadBadgeImage,
  getSkillxAssignments,
  clearSkillxAssignments,
} from '../../../redux/features/achievementBadgeSlice';
import { getAvailableCourses } from '../../../redux/features/coursesSlice';
import { resolveBadgeImage } from './resolveBadgeImage';

// Upload limit enforced client-side to fail fast (backend caps at 4 MB too).
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

// Add / Edit a single achievement badge. `row` present → edit mode.
const AchievementBadgeAddEdit = ({ row, onClose }) => {
  const dispatch = useDispatch();
  const { mutating, uploading, assignments } = useSelector((state) => state.achievementBadge);
  const { availableCourse, loading: coursesLoading } = useSelector((state) => state.courses);
  const handleApiResponse = useHandleApiResponse();
  const { enqueueSnackbar } = useSnackbar();

  // Load skillx courses for the picker; on edit, preload the row's assignments
  // so the assignment autocomplete can resolve its label.
  useEffect(() => {
    dispatch(getAvailableCourses({ skillx: true }));
    if (row?.course_id) {
      dispatch(getSkillxAssignments({ courseId: row.course_id }));
    } else {
      dispatch(clearSkillxAssignments());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.course_id]);

  const initialValues = {
    badge_name: row?.badge_name ?? '',
    badge_description: row?.badge_description ?? '',
    badge_image: row?.badge_image ?? '',
    course_id: row?.course_id ?? '',
    assignment_id: row?.assignment_id ?? '',
  };

  const schema = Yup.object({
    badge_name: Yup.string().trim().required('Badge name is required'),
    badge_description: Yup.string().nullable(),
    badge_image: Yup.string().nullable(),
    course_id: Yup.number().nullable(),
    assignment_id: Yup.number().nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const payload = {
        badge_name: values.badge_name.trim(),
        // Optional: send null (not "") so an emptied field clears server-side.
        badge_description: values.badge_description?.trim() ? values.badge_description.trim() : null,
        badge_image: values.badge_image?.trim() ? values.badge_image.trim() : null,
        course_id: values.course_id ? Number(values.course_id) : null,
        assignment_id: values.assignment_id ? Number(values.assignment_id) : null,
      };

      let res;
      if (row) {
        // UPDATE — badge_id goes in the body (coerced to number inside the slice).
        res = await dispatch(updateAchievementBadges({ badge_id: row.badge_id, ...payload }));
      } else {
        // CREATE — slice wraps this into { data: [ ... ] }.
        res = await dispatch(createAchievementBadges(payload));
      }

      handleApiResponse(res, {
        onSuccess: () => {
          formik.resetForm();
          if (onClose) onClose(true); // refetch the table
        },
      });
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    if (onClose) onClose(false);
  };

  // Upload the picked file, then store the returned bare filename as badge_image.
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = null; // allow re-picking the same file
    if (!file) return;

    if (file.size > MAX_UPLOAD_BYTES) {
      enqueueSnackbar('File is too large. Maximum size is 4 MB.', { variant: 'error' });
      return;
    }

    const res = await dispatch(uploadBadgeImage(file));
    const filename = res?.payload?.data?.filename;
    if (res?.payload?.status === 'success' && filename) {
      formik.setFieldValue('badge_image', filename);
    } else {
      enqueueSnackbar(res?.payload?.message || 'Image upload failed', { variant: 'error' });
    }
  };

  const removeImage = () => formik.setFieldValue('badge_image', '');

  // Course selected → store id, reset the assignment, and load that course's
  // assignments. Clearing the course clears both the assignment and the list.
  const handleCourseChange = (event, option) => {
    const courseId = option?.id ?? '';
    formik.setFieldValue('course_id', courseId);
    formik.setFieldValue('assignment_id', '');
    if (courseId) {
      dispatch(getSkillxAssignments({ courseId }));
    } else {
      dispatch(clearSkillxAssignments());
    }
  };

  const handleAssignmentChange = (event, option) => {
    formik.setFieldValue('assignment_id', option?.id ?? '');
  };

  const selectedCourse = availableCourse?.find((c) => c.id === formik.values.course_id) || null;
  const selectedAssignment = assignments?.data?.find((a) => a.id === formik.values.assignment_id) || null;

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            id="badge_name"
            name="badge_name"
            label="Badge Name"
            value={formik.values.badge_name}
            onChange={formik.handleChange}
            error={formik.touched.badge_name && Boolean(formik.errors.badge_name)}
            helperText={formik.touched.badge_name && formik.errors.badge_name}
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            id="badge_description"
            name="badge_description"
            label="Description"
            value={formik.values.badge_description}
            onChange={formik.handleChange}
            error={formik.touched.badge_description && Boolean(formik.errors.badge_description)}
            helperText={formik.touched.badge_description && formik.errors.badge_description}
          />

          {/* Course — skillx courses; selecting one loads its assignments */}
          <Autocomplete
            options={availableCourse || []}
            loading={coursesLoading}
            getOptionLabel={(option) => option?.courseName || ''}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={selectedCourse}
            onChange={handleCourseChange}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                autoComplete="off"
                label="Course"
                error={formik.touched.course_id && Boolean(formik.errors.course_id)}
                helperText={formik.touched.course_id && formik.errors.course_id}
              />
            )}
          />

          {/* Skillx assignment — options depend on the selected course */}
          <Autocomplete
            options={assignments?.data || []}
            loading={assignments?.loading}
            disabled={!formik.values.course_id}
            getOptionLabel={(option) => option?.title || ''}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={selectedAssignment}
            onChange={handleAssignmentChange}
            noOptionsText={
              !formik.values.course_id ? 'Select a course first' : 'No assignments for this course'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                autoComplete="off"
                label="Skillx Assignment"
                error={formik.touched.assignment_id && Boolean(formik.errors.assignment_id)}
                helperText={
                  (formik.touched.assignment_id && formik.errors.assignment_id) ||
                  (!formik.values.course_id ? 'Select a course to load assignments' : '')
                }
              />
            )}
          />

          {/* Badge image — upload to /general/upload-badge, store the filename */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Badge Image
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              {formik.values.badge_image ? (
                <Box sx={{ position: 'relative', width: 72, height: 72 }}>
                  <img
                    src={resolveBadgeImage(formik.values.badge_image)}
                    alt="badge preview"
                    style={{
                      width: 72,
                      height: 72,
                      objectFit: 'contain',
                      borderRadius: 8,
                      border: '1px solid #e0e0e0',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={removeImage}
                    disabled={uploading}
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: -10,
                      bgcolor: 'error.main',
                      color: '#fff',
                      '&:hover': { bgcolor: 'error.dark' },
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 8,
                    border: '1px dashed #c4c4c4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.disabled',
                    fontSize: 12,
                  }}
                >
                  No image
                </Box>
              )}

              <Button
                component="label"
                variant="outlined"
                size="small"
                disabled={uploading}
                startIcon={
                  uploading ? (
                    <CircularProgress size={16} />
                  ) : formik.values.badge_image ? (
                    <SwapHorizIcon fontSize="small" />
                  ) : (
                    <AttachFileIcon fontSize="small" />
                  )
                }
                sx={{ textTransform: 'none', borderStyle: 'dashed', borderRadius: 2 }}
              >
                {uploading ? 'Uploading...' : formik.values.badge_image ? 'Change Image' : 'Upload Image'}
                <input type="file" hidden accept="image/*,.pdf" onChange={handleImageUpload} />
              </Button>
            </Stack>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary' }}>
              Images or PDF, up to 4 MB.
            </Typography>
          </Box>

          {mutating && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Stack direction="row" spacing={1}>
              <CancelButton action={handleCancel}>Cancel</CancelButton>
              {row ? (
                <UpdateButton disabled={mutating || uploading} type="submit">
                  {mutating ? 'Updating...' : 'Update'}
                </UpdateButton>
              ) : (
                <SubmitButton disabled={mutating || uploading} type="submit">
                  {mutating ? 'Submitting...' : 'Submit'}
                </SubmitButton>
              )}
            </Stack>
          </Box>
        </Stack>
      </Form>
    </FormikProvider>
  );
};

export default AchievementBadgeAddEdit;
